import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import { Image } from 'expo-image';
import { Play, ChevronDown, ChevronUp, Heart } from 'lucide-react-native';
import { Serie } from '@/types';
import { useFavorites } from '@/hooks/useFavorites';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { useBranding } from '@/contexts/BrandingContext';
import { ensureWebpFormat } from '@/utils/imageResizer';

const blurhash = 'L6PZfSi_.AyE_3t7t7R**0o#DgR4';

interface SerieCardHoverOverlayProps {
  serie: Serie;
  onPlay: () => void;
  cardWidth: number;
  t: (key: any) => string;
}

export const SerieCardHoverOverlay: React.FC<SerieCardHoverOverlayProps> = ({
  serie,
  onPlay,
  cardWidth,
  t,
}) => {
  const overlayWidth = cardWidth * 1.45;
  const imageHeight = serie.thumbnailLandscape
    ? overlayWidth * (9 / 16)
    : overlayWidth * (3 / 2);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const { toggleFavorite, isFavorite } = useFavorites();
  const { authState } = useAuth();
  const { colors: { primary: primaryColor } } = useTheme();
  const { uiOverrides } = useBranding();
  const [isFav, setIsFav] = React.useState(false);

  React.useEffect(() => {
    if (!authState.user?.smartuserId) return;
    let mounted = true;
    isFavorite(parseInt(serie.id), 'serie').then(status => {
      if (mounted) setIsFav(status);
    });
    return () => { mounted = false; };
  }, [serie.id, authState.user?.smartuserId, isFavorite]);

  const handleFavoritePress = async () => {
    if (!authState.user) {
      Alert.alert(t('signInRequired'), t('pleaseSignInToAddSerieFavorite'));
      return;
    }
    const result = await toggleFavorite(parseInt(serie.id), 'serie');
    if (result.success) {
      setIsFav(result.isFavoriteAfter);
    }
  };

  return (
    <View style={[styles.container, { width: overlayWidth }]}>
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={onPlay}
        style={[styles.imageWrapper, { height: imageHeight, cursor: 'pointer' } as any]}
      >
        <Image
          source={{ uri: ensureWebpFormat(serie.thumbnailLandscape || serie.thumbnail) }}
          style={styles.image}
          placeholder={{ blurhash }}
          contentFit="cover"
          transition={150}
          cachePolicy="memory-disk"
          recyclingKey={`hover-${serie.id}`}
          accessibilityLabel={serie.title}
        />
        {serie.isNew && (
          <View style={[styles.badge, styles.newBadge, uiOverrides.badge_new_color ? { backgroundColor: uiOverrides.badge_new_color } : undefined]}>
            <Text style={styles.badgeText}>{t('new')}</Text>
          </View>
        )}
        {serie.isTrending && (
          <View style={[styles.badge, styles.trendingBadge, { backgroundColor: uiOverrides.badge_trending_color || primaryColor }]}>
            <Text style={styles.badgeText}>{t('trending')}</Text>
          </View>
        )}
      </TouchableOpacity>

      <View style={styles.infoPanel}>
        <View style={styles.actionRow}>
          <TouchableOpacity style={styles.playBtn} onPress={onPlay}>
            <Play size={18} color="#000" fill="#000" />
          </TouchableOpacity>
          {authState.user && (
            <TouchableOpacity style={styles.circleBtn} onPress={handleFavoritePress}>
              <Heart
                size={16}
                color={isFav ? primaryColor : '#ffffff'}
                fill={isFav ? primaryColor : 'transparent'}
              />
            </TouchableOpacity>
          )}
          <View style={styles.spacer} />
          <TouchableOpacity
            style={styles.circleBtn}
            onPress={() => setIsDescriptionExpanded(prev => !prev)}
          >
            {isDescriptionExpanded ? (
              <ChevronUp size={16} color="#ffffff" />
            ) : (
              <ChevronDown size={16} color="#ffffff" />
            )}
          </TouchableOpacity>
        </View>

        <View style={styles.metaRow}>
          <Text style={styles.metaText}>
            {serie.totalSeasons} {serie.totalSeasons > 1 ? t('seasons') : t('season')}
          </Text>
        </View>

        <Text style={styles.title} numberOfLines={1}>{serie.title}</Text>
        <Text
          style={styles.description}
          numberOfLines={isDescriptionExpanded ? undefined : 2}
        >
          {serie.description}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#1a1a1a',
  },
  imageWrapper: {
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  badge: {
    position: 'absolute',
    top: 8,
    left: 8,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  newBadge: {
    backgroundColor: '#00D4AA',
  },
  trendingBadge: {},
  badgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '600',
    lineHeight: 14,
  },
  infoPanel: {
    padding: 12,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  playBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  circleBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  spacer: {
    flex: 1,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  },
  metaText: {
    color: '#aaa',
    fontSize: 11,
  },
  title: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 4,
  },
  description: {
    color: '#999',
    fontSize: 11,
    lineHeight: 15,
  },
});
